import { isSalesforceFeatureAllowed } from "../../../../utils/salesforceMPUtils";

// Version constants for different features
export const FIRMOGRAPHICS_ALLOWED_MP_VERSION = "1.1.0";
export const ACCOUNT_SCORING_ALLOWED_MP_VERSION = "1.3.0";
export const IT_SPEND_ALLOWED_MP_VERSION = "1.10.0";
export const CUSTOM_HGID_MATCHING_ALLOWED_MP_VERSION = "1.11.0";

// Version checking functions
const isSalesforceRecordTypesAllowed = (_version?: string) => true; // Always allowed
const isSalesforceProductInstallsAllowed = (_version?: string) => true; // Always allowed
const isSalesforceFirmographicsAllowed = (version?: string) =>
  isSalesforceFeatureAllowed(version, FIRMOGRAPHICS_ALLOWED_MP_VERSION);
const isSalesforceAccountScoringAllowed = (version?: string) =>
  isSalesforceFeatureAllowed(version, ACCOUNT_SCORING_ALLOWED_MP_VERSION);
const isSalesforceItSpendAllowed = (version?: string) =>
  isSalesforceFeatureAllowed(version, IT_SPEND_ALLOWED_MP_VERSION);
const isSalesforceLocalizedInstallationAllowed = (_version?: string) => true; // Always allowed
const isSalesforceCountryLevelMatchingAllowed = (_version?: string) => true; // Always allowed
const isSalesforceHGIDMatchingAllowed = (version?: string) =>
  isSalesforceFeatureAllowed(version, CUSTOM_HGID_MATCHING_ALLOWED_MP_VERSION);

export const DEFAULT_COUNTRY_MATCHING_FIELD = "BillingCountry";

// Page fallback messages
export const FALLBACK_MESSAGES = {
  configureSettingsTitle: "Configure your settings",
  noIntegrationFound: "No Salesforce integration found",
} as const;

// Auto Sync section constants
export const AUTO_SYNC = {
  title: "Auto Sync HG with Salesforce",
  tooltip:
    "When enabled your account data is automatically synchronized to Salesforce",
  sandboxLimitationMessage:
    "For sandbox orgs, auto sync will be disabled after 14 days.",
} as const;

// Country Matching Radio button labels
export const FIELD_LABELS = {
  billingCountry: "Billing Country",
  shippingCountry: "Shipping Country",
} as const;

// Validation messages
export const VALIDATION_MESSAGES = {
  title: "Validation Error",
  noIntegrationOrSettings: "No integration or settings found",
  recordTypesRequired:
    "Please select at least one record type (Accounts or Leads)",
  enrichmentRequired: "Please select at least one data enrichment option",
  noEnrichmentFeaturesAvailable:
    "No enrichment features are available for your managed package version",
  fallbackMessage:
    "At least one check box from both Match Accounts & Leads and Enrich All HG Data has to be enabled.",
} as const;

// Action messages
export const ACTION_MESSAGES = {
  firstSaveSettingsSuccess:
    "Settings saved successfully! Sync will run automatically in few seconds.",
  saveSettingsSuccess: "Settings saved successfully!",
  saveSettingsError: "Failed to save settings. Please try again.",
  deleteSuccess: "Integration deleted successfully!",
  deleteError: "Failed to delete integration:",
  integrationCancelled:
    "Integration cancelled, you'll be redirected to the integrations page or to existing integration.",
} as const;

// Centralized feature configuration for Salesforce Integration settings
export const FEATURE_CONFIG = {
  recordTypes: {
    fields: [
      { name: "syncAccounts", label: "Accounts" },
      { name: "syncLeads", label: "Leads" },
    ],
    title: "Record Types",
    tooltip:
      "This integration will use the Website and Company Name fields during matching. You have the option to enable Country as an additional input field. For Accounts, you will be prompted to choose either Billing Country or Shipping Country for this process.",
    versionCheck: isSalesforceRecordTypesAllowed,
  },
  hgDataEnrichment: {
    title: "HG Data Enrichment",
    tooltip:
      "Select which HG data types to synchronize to your Salesforce records",
    fields: [
      {
        name: "exportTechInstalls",
        label: "Product Installs",
        versionCheck: isSalesforceProductInstallsAllowed,
      },
      {
        name: "exportItSpend",
        label: "IT Spend",
        versionCheck: isSalesforceItSpendAllowed,
      },
      {
        name: "exportFirmographics",
        label: "Firmographics",
        versionCheck: isSalesforceFirmographicsAllowed,
      },
      {
        name: "exportAccountScoring",
        label: "Account Scoring",
        versionCheck: isSalesforceAccountScoringAllowed,
      },
    ],
  },
  localizedInstallation: {
    fields: [{ name: "localizeInstalls" }],
    title: "Localized Installation",
    tooltip:
      "Disabling this option will return companies if an install is detected anywhere in the company's global footprint, even outside the country of the company",
    description:
      "Only return companies if the install data is detected in the same country as the company",
    versionCheck: isSalesforceLocalizedInstallationAllowed,
  },
  countryLevelMatching: {
    fields: [{ name: "useCountryLevelMatching" }],
    title: "Country Level Matching",
    tooltip:
      "This integration will use the Website and Company Name fields during matching. You have the option to enable Country as an additional input field. For Accounts, you will be prompted to choose either Billing Country or Shipping Country for this process.",
    description: "Match companies using their Billing or Shipping country",
    versionCheck: isSalesforceCountryLevelMatchingAllowed,
  },
  hgIdMatching: {
    fields: [{ name: "customHgIdMatching" }],
    title: "HG ID Matching",
    tooltip:
      "Before enabling this feature, please create a custom field with the field name HGInsights_CustomerManaged_HGID and permission access. See our knowledge base for more detailed instructions.",
    description:
      "Please note, this feature requires the creation of a custom field in your Salesforce.",
    versionCheck: isSalesforceHGIDMatchingAllowed,
  },
} as const;
